import { isBotUA } from '@/utils/bot-detection';
import logger from '@/utils/client-logger';
import {
  DEFAULT_NETWORK_SNAPSHOT,
  getNetworkSnapshot,
  getBrowserNetworkSnapshotSource,
  getNetworkDownlinkSource,
  type NetworkDownlinkSource,
  type NetworkSnapshot,
} from './networkSnapshot';
import type { PerformancePageType } from './pageType';
import type { NavigationTimingLike } from './networkSnapshot';
import type { NavigationAssociation } from './navigationDiagnostics';

export const FRONTEND_PERFORMANCE_EVENT_NAME = 'FrontendPerformance_Monitor';
export const FRONTEND_PERFORMANCE_METRIC_VERSION = 2;

export type FrontendPerformanceMetricName = 'FCP' | 'LCP' | 'TTFB' | 'CLS' | 'INP' | 'TBT';

export interface PerformanceMetricLike {
  navigationType?: string;
  entries?: readonly NavigationTimingLike[] | readonly PerformanceEntry[];
  id?: string;
  value: number;
  delta?: number;
  attribution?: {
    largestShiftTarget?: string;
    interactionTarget?: string;
  };
}

export interface MetricPayloadSource {
  pageUrl?: string;
  visibilityState?: string;
  devicePixelRatio?: number;
  viewportWidth?: number;
  viewportHeight?: number;
  userAgent?: string;
  networkSnapshot?: NetworkSnapshot;
  networkDownlinkSource?: NetworkDownlinkSource;
}

export interface FrontendPerformanceBasePayload {
  nav_id?: string;
  nav_lifecycle_index?: number;
  xn_metric_version: number;
  /** 与 xn_page_type 对应的文档落地 URL；神策内置 $url 保持 SDK 默认行为。 */
  xn_page_url: string;
  xn_page_type: PerformancePageType;
  xn_type: FrontendPerformanceMetricName;
  xn_value: number;
  xn_id: string;
  xn_delta: number;
  xn_data: string;
  xn_page_visible: string;
  xn_dpr: number;
  xn_viewport_w: number;
  xn_viewport_h: number;
  xn_is_spider: boolean;
}

export type FrontendPerformanceLcpPayload = FrontendPerformanceBasePayload &
  NetworkSnapshot & {
    xn_api_log: string;
  };

export type FrontendPerformancePayload =
  | FrontendPerformanceBasePayload
  | FrontendPerformanceLcpPayload;

export function normalizeMetricValue(
  metricName: FrontendPerformanceMetricName,
  value: number,
): number {
  if (metricName === 'CLS') {
    return Math.round(value * 1000);
  }

  return Math.round(value);
}

export function getBrowserMetricPayloadSource(): MetricPayloadSource {
  let networkSnapshot = DEFAULT_NETWORK_SNAPSHOT;
  let networkDownlinkSource: NetworkDownlinkSource = 'unavailable';

  try {
    const source = getBrowserNetworkSnapshotSource();
    networkSnapshot = getNetworkSnapshot(source);
    try {
      networkDownlinkSource = getNetworkDownlinkSource(source);
    } catch {
      // Optional diagnostic metadata must not discard a successfully captured legacy snapshot.
    }
  } catch (error) {
    logger.error('FrontendPerformance network snapshot failed:', error);
    networkSnapshot = DEFAULT_NETWORK_SNAPSHOT;
  }

  return {
    pageUrl: typeof window !== 'undefined' ? window.location.href : '',
    visibilityState: typeof document !== 'undefined' ? document.visibilityState : 'hidden',
    devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : 1,
    viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
    viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 0,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    networkSnapshot,
    networkDownlinkSource,
  };
}

function buildAttributionData(
  metricName: FrontendPerformanceMetricName,
  metric: PerformanceMetricLike,
  normalizedValue: number,
): string {
  if (metricName !== 'CLS' && metricName !== 'INP') {
    return '';
  }

  const target =
    metricName === 'CLS'
      ? (metric.attribution?.largestShiftTarget ?? '')
      : (metric.attribution?.interactionTarget ?? '');

  return JSON.stringify({
    name: metricName,
    value: normalizedValue,
    target,
  });
}

export function buildMetricPayload(options: {
  metricName: FrontendPerformanceMetricName;
  metric: PerformanceMetricLike;
  pageType: PerformancePageType;
  source?: MetricPayloadSource;
  navigation?: NavigationAssociation;
}): FrontendPerformancePayload {
  const source = options.source ?? getBrowserMetricPayloadSource();
  const normalizedValue = normalizeMetricValue(options.metricName, options.metric.value);
  const normalizedDelta = normalizeMetricValue(
    options.metricName,
    options.metric.delta ?? options.metric.value,
  );

  const payload: FrontendPerformancePayload = {
    ...options.navigation,
    xn_metric_version: FRONTEND_PERFORMANCE_METRIC_VERSION,
    xn_page_url: source.pageUrl ?? '',
    xn_page_type: options.pageType,
    xn_type: options.metricName,
    xn_value: normalizedValue,
    xn_id: options.metric.id ?? '',
    xn_delta: normalizedDelta,
    xn_data: buildAttributionData(options.metricName, options.metric, normalizedValue),
    xn_page_visible: source.visibilityState ?? 'hidden',
    xn_dpr: source.devicePixelRatio ?? 1,
    xn_viewport_w: source.viewportWidth ?? 0,
    xn_viewport_h: source.viewportHeight ?? 0,
    xn_is_spider: isBotUA(source.userAgent ?? ''),
  };

  if (options.metricName === 'LCP') {
    Object.assign(payload, source.networkSnapshot ?? DEFAULT_NETWORK_SNAPSHOT, {
      xn_api_log: '[]',
    });
  }

  return payload;
}
